import { useContext } from "react"
import { EditorContent, EditorContext, useEditor } from "@tiptap/react"
import type { Content, Editor } from "@tiptap/react"

// --- Tiptap Core Extensions ---
import { StarterKit } from "@tiptap/starter-kit"
import { TaskList, TaskItem } from "@tiptap/extension-list"
import { Color, TextStyle } from "@tiptap/extension-text-style"
import { Placeholder, Selection } from "@tiptap/extensions"
import { Typography } from "@tiptap/extension-typography"
import { Highlight } from "@tiptap/extension-highlight"
import { Superscript } from "@tiptap/extension-superscript"
import { Subscript } from "@tiptap/extension-subscript"
import { TextAlign } from "@tiptap/extension-text-align"
import { Mathematics } from "@tiptap/extension-mathematics"
import { UniqueID } from "@tiptap/extension-unique-id"
import { Emoji, gitHubEmojis } from "@tiptap/extension-emoji"
import { Details, DetailsContent, DetailsSummary } from "@tiptap/extension-details"
import {
  getHierarchicalIndexes,
  TableOfContents,
} from "@tiptap/extension-table-of-contents"

// --- Hooks ---
import { useUiEditorState } from "@/hooks/use-ui-editor-state"
import { useScrollToHash } from "@/components/tiptap-ui/copy-anchor-link-button/use-scroll-to-hash"

// --- Custom Extensions ---
import { HorizontalRule } from "@/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension"
import { UiState } from "@/components/tiptap-extension/ui-state-extension"
import { Image } from "@/components/tiptap-node/image-node/image-node-extension"
import { NodeBackground } from "@/components/tiptap-extension/node-background-extension"
import { NodeAlignment } from "@/components/tiptap-extension/node-alignment-extension"
import { TocNode } from "@/components/tiptap-node/toc-node/extensions/toc-node-extension"

// --- Tiptap Node ---
import { ImageUploadNode } from "@/components/tiptap-node/image-upload-node/image-upload-node-extension"
import { AmazonProductNode } from "@/components/tiptap-node/amazon-product-node"
import { PdfNode } from "@/components/tiptap-node/pdf-node"
import { CalloutNode } from "@/components/tiptap-node/callout-node"
import { TodoLinkNode } from "@/components/tiptap-node/todo-link-node"
import { HighlightRefNode } from "@/components/tiptap-node/highlight-ref-node"
import { DocLinkNode } from "@/components/tiptap-node/doc-link-node"

// --- Table Node ---
import { TableKit } from "@/components/tiptap-node/table-node/extensions/table-node-extension"
import { TableHandleExtension } from "@/components/tiptap-node/table-node/extensions/table-handle"
import { TableHandle } from "@/components/tiptap-node/table-node/ui/table-handle/table-handle"
import { TableSelectionOverlay } from "@/components/tiptap-node/table-node/ui/table-selection-overlay"
import { TableCellHandleMenu } from "@/components/tiptap-node/table-node/ui/table-cell-handle-menu"
import { TableExtendRowColumnButtons } from "@/components/tiptap-node/table-node/ui/table-extend-row-column-button"
import "@/components/tiptap-node/table-node/styles/prosemirror-table.scss"
import "@/components/tiptap-node/table-node/styles/table-node.scss"

import "@/components/tiptap-node/blockquote-node/blockquote-node.scss"
import "@/components/tiptap-node/code-block-node/code-block-node.scss"
import "@/components/tiptap-node/horizontal-rule-node/horizontal-rule-node.scss"
import "@/components/tiptap-node/list-node/list-node.scss"
import "@/components/tiptap-node/image-node/image-node.scss"
import "@/components/tiptap-node/heading-node/heading-node.scss"
import "@/components/tiptap-node/paragraph-node/paragraph-node.scss"

// --- Tiptap UI ---
import { EmojiDropdownMenu } from "@/components/tiptap-ui/emoji-dropdown-menu"
import { SlashDropdownMenu } from "@/components/tiptap-ui/slash-dropdown-menu"
import { DocLinkDropdownMenu } from "@/components/tiptap-ui/doc-link-dropdown-menu"
import { DragContextMenu } from "@/components/tiptap-ui/drag-context-menu"

// --- Lib ---
import { handleImageUpload, handleImageUploadFromUrl, MAX_FILE_SIZE } from "@/lib/tiptap-utils"
import { NotionKeymap } from "@/components/shared/notion-keymap-extension"

// --- Styles ---
import "@/styles/_variables.scss"
import "@/styles/_keyframe-animations.scss"
import "@/components/tiptap-templates/notion-like/notion-like-editor.scss"

// --- Content ---
import { NotionToolbarFloating } from "@/components/tiptap-templates/notion-like/notion-like-editor-toolbar-floating"
import { MobileToolbar } from "@/components/tiptap-templates/notion-like/notion-like-editor-mobile-toolbar"
import { LinkBubble } from "@/components/shared/link-bubble"
import { TocSidebar } from "@/components/tiptap-node/toc-node"
import { useToc } from "@/components/tiptap-node/toc-node/context/toc-context"
import { ListNormalizationExtension } from "@/components/tiptap-extension/list-normalization-extension"
import { Indent } from "@/components/tiptap-extension/indent-extension"
import { TripleClickBlockSelection } from "@/components/tiptap-extension/triple-click-block-selection-extension"
import { FileDropPaste } from "@/components/tiptap-extension/file-drop-paste-extension"
import { selectCanUseMemo, useAppStore } from "@/store/useAppStore"

export interface EditorProviderProps {
  /** エディタ生成時に設定する初期コンテンツ (Tiptap JSON / HTML / 文字列)。
   *  setContent を使わずこちらを使うことで undo スタックに初期読み込みが残らず、
   *  初期表示状態より前に undo されずに済む。 */
  content?: Content
  /** 編集可能か */
  editable?: boolean
  /** プレースホルダ */
  placeholder?: string
  /** エディタ生成時のコールバック */
  onCreate?: (props: { editor: Editor }) => void
  /** 内容更新時のコールバック */
  onUpdate?: (props: { editor: Editor }) => void
}

/**
 * Loading spinner component shown while the editor is initializing
 */
export function LoadingSpinner({ text = "Loading..." }: { text?: string }) {
  return (
    <div className="spinner-container">
      <div className="spinner-content">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <div className="spinner-loading-text">{text}</div>
      </div>
    </div>
  )
}

/**
 * EditorContent component that renders the actual editor
 */
export function EditorContentArea() {
  const { editor } = useContext(EditorContext)!
  const { isDragging } = useUiEditorState(editor)
  // [[ のリンク候補・メモ作成は memo 権限が要る（TODO メモだけ使える人には出さない）
  const canUseMemo = useAppStore(selectCanUseMemo)

  useScrollToHash()

  if (!editor) {
    return null
  }

  return (
    <EditorContent
      editor={editor}
      role="presentation"
      className="notion-like-editor-content"
      style={{
        cursor: isDragging ? "grabbing" : "auto",
      }}
    >
      <DragContextMenu />
      <EmojiDropdownMenu />
      <SlashDropdownMenu />
      {canUseMemo && <DocLinkDropdownMenu />}
      <NotionToolbarFloating />
      <LinkBubble />
    </EditorContent>
  )
}

/**
 * Component that creates and provides the editor instance.
 *
 * シングルユーザー想定の Tiptap エディタ。履歴(undo/redo)は StarterKit の History を使用し、
 * 本文の永続化は親コンポーネントが onUpdate コールバックで HTTP 保存する。
 */
export function EditorProvider(props: EditorProviderProps) {
  const {
    content,
    editable = true,
    placeholder = "入力してください…",
    onCreate,
    onUpdate,
  } = props

  const { setTocContent } = useToc()

  const editor = useEditor({
    immediatelyRender: false,
    editable,
    content,
    onCreate,
    onUpdate,
    editorProps: {
      attributes: {
        class: "notion-like-editor",
      },
    },
    extensions: [
      StarterKit.configure({
        horizontalRule: false,
        dropcursor: {
          width: 2,
        },
        link: { openOnClick: false },
      }),
      HorizontalRule,
      // 配置のキーボードショートカット (⌘⇧L/E/R/J) は使わない
      TextAlign.extend({ addKeyboardShortcuts: () => ({}) }).configure({
        types: ["heading", "paragraph"],
      }),
      Placeholder.configure({
        placeholder,
        emptyNodeClass: "is-empty with-slash",
      }),
      Emoji.configure({
        emojis: gitHubEmojis.filter(
          (emoji) => !emoji.name.includes("regional")
        ),
        forceFallbackImages: true,
      }),
      TableKit.configure({
        table: {
          resizable: true,
          cellMinWidth: 120,
        },
      }),
      NodeBackground.configure({
        types: [
          "paragraph",
          "heading",
          "blockquote",
          "taskList",
          "bulletList",
          "orderedList",
          "tableCell",
          "tableHeader",
          "tocNode",
        ],
      }),
      NodeAlignment,
      TextStyle,
      Mathematics,
      Superscript,
      Subscript,
      Indent,
      Color,
      TaskList,
      TaskItem.configure({ nested: true }),
      Details.configure({ persist: true, openClassName: "is-open" }),
      DetailsSummary,
      DetailsContent,
      Highlight.configure({ multicolor: true }),
      Selection,
      Image,
      TableOfContents.configure({
        getIndex: getHierarchicalIndexes,
        onUpdate(content) {
          setTocContent(content)
        },
      }),
      TableHandleExtension,
      ListNormalizationExtension,
      TripleClickBlockSelection,
      ImageUploadNode.configure({
        accept: "image/*",
        maxSize: MAX_FILE_SIZE,
        limit: 3,
        upload: handleImageUpload,
        uploadFromUrl: handleImageUploadFromUrl,
        onError: (error) => console.error("Upload failed:", error),
      }),
      UniqueID.configure({
        types: [
          "table",
          "paragraph",
          "bulletList",
          "orderedList",
          "taskList",
          "heading",
          "blockquote",
          "codeBlock",
          "tocNode",
        ],
      }),
      Typography,
      UiState,
      NotionKeymap,
      TocNode.configure({
        topOffset: 48,
      }),
      AmazonProductNode,
      PdfNode,
      CalloutNode,
      TodoLinkNode,
      HighlightRefNode,
      DocLinkNode,
      FileDropPaste,
    ],
  })

  if (!editor) {
    return <LoadingSpinner />
  }

  return (
    <div className="notion-like-editor-wrapper">
      <EditorContext.Provider value={{ editor }}>
        <div className="notion-like-editor-layout">
          <EditorContentArea />
          <TocSidebar topOffset={48} />
        </div>

        <TableExtendRowColumnButtons />
        <TableHandle />
        <TableSelectionOverlay
          showResizeHandles={true}
          cellMenu={(cellMenuProps) => (
            <TableCellHandleMenu
              editor={cellMenuProps.editor}
              onMouseDown={(e) => cellMenuProps.onResizeStart?.("br")(e)}
            />
          )}
        />
        <MobileToolbar />
      </EditorContext.Provider>
    </div>
  )
}
